/** @jsxImportSource @emotion/react */
import { useState, useMemo, forwardRef } from 'react'
import { Box, TextField, Typography, Alert } from '@mui/material'
import { Button } from '@app/components/Atoms/Button'
import { useLayers } from '@app/contexts/LayerContext'
import { useLayerDataFetcher } from '@app/components/Molecules/RiskSideDrawerContent/data_fetchers/layerDataFetcher'
import type { DialogData, MapLayer, MapLayerType } from '@app/types'

const buildLayerIndex = (dialogData: DialogData): Map<string, MapLayer | string> => {
  // Make a lookup that maps tileset ids to assetTag/type
  // and assetTag/type to Maplayer so we can access MapLayer objects using both ids
  // Eg:
  // 'urbanintelligence.e16_l0_v7_c10' => 'e16_l0_v7'
  // 'e16_l0_v7' => {MapLayer}

  const index = new Map<string, MapLayer | string>()

  dialogData.hazardDialogData.hazardDetails.forEach((hazard) => {
    hazard.scenarios.forEach((scenario) => {
      scenario.tilesets.forEach((tileset) => {
        index.set(tileset.id, scenario.assetTag)
      })

      index.set(scenario.assetTag, {
        ...scenario,
        layerType: 'hazard' as MapLayerType,
        visible: true,
      })
    })
  })

  Object.values(dialogData.assetDialogData).forEach((domain) => {
    domain.groups.forEach((group) => {
      group.assets.forEach((asset) => {
        asset.tilesets.forEach((tileset) => {
          index.set(tileset.id, asset.type)
        })

        index.set(asset.type, { ...asset, layerType: 'asset' as MapLayerType, visible: true })
      })
    })
  })

  Object.values(dialogData.informationDialogData).forEach((menu) => {
    menu.groups.forEach((group) => {
      group.layers.forEach((layer) => {
        layer.tilesets.forEach((tileset) => {
          index.set(tileset.id, layer.id)
        })

        index.set(layer.id, { ...layer, layerType: 'information' as MapLayerType, visible: true })
      })
    })
  })

  return index
}

const parseTilesetIds = (text: string): string[] =>
  text
    .replaceAll(/['"]/g, '')
    .split(/[\n,-\s]+/)
    .map((s) => s.trim())
    .filter(Boolean)

interface Props {
  onClose: () => void
}

export const LayerImportDialog = forwardRef<HTMLDivElement, Props>(({ onClose }, ref) => {
  const dialogData = useLayerDataFetcher()
  const { prepareAndUpdateLayers } = useLayers()
  const [text, setText] = useState('')

  const layerIndex = useMemo(
    () => (dialogData ? buildLayerIndex(dialogData) : new Map<string, MapLayer>()),
    [dialogData],
  )

  const { found, notFound } = useMemo(() => {
    if (!text.trim()) return { found: [], notFound: [] }

    const tilesetIds = parseTilesetIds(text)
    const found: MapLayer[] = []
    const notFound: string[] = []

    for (const tilesetId of tilesetIds) {
      let layer = layerIndex.get(tilesetId)

      if (layer) {
        // layer is an assetTag/type if looking up by tileset id so get the MapLayer with that
        if (typeof layer == 'string') layer = layerIndex.get(layer) as MapLayer

        found.push({
          ...layer,
          id: layer.tilesets.map((t) => t.id).join('-'),
        })
      } else {
        notFound.push(tilesetId)
      }
    }

    const uniqueFound = uniqueBy(found, (item) => (item.assetTag ? item.assetTag : item.type))

    return { found: uniqueFound, notFound }
  }, [text, layerIndex])

  const handleImport = () => {
    if (!found.length) return

    prepareAndUpdateLayers({ layers: found, activeHazardParameters: 'onlyslr' })
    onClose()
  }

  return (
    <Box
      ref={ref}
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: 3,
        width: 480,
        bgcolor: 'background.paper',
        borderRadius: 1,
        boxShadow: 24,
      }}
    >
      <Typography variant="subtitle2">Import layers</Typography>
      <Typography variant="body2" color="text.secondary">
        Paste tileset IDs, asset tags or layer ids
      </Typography>
      <TextField
        multiline
        minRows={6}
        maxRows={14}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder='e900_l0_v3,"e889_l0_v3"
urbanintelligence.e16_l0_v7_c10,
urbanintelligence.e868_l0_v3_c10-urbanintelligence.e872_l0_v5_c10
          ...
        '
        size="small"
        autoFocus
      />
      {text.trim() && (
        <Alert severity={notFound.length ? 'warning' : 'success'} sx={{ py: 0.5 }}>
          {found.length} layer{found.length !== 1 ? 's' : ''} matched
          {notFound.length > 0 && `, ${notFound.length} not found`}
          {notFound.length > 0 && found.length == 0 && `, Are you on the right client?`}
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button variant="outlined" color="grey" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleImport} disabled={!found.length}>
          Import {found.length > 0 ? `${found.length} layers` : ''}
        </Button>
      </Box>
    </Box>
  )
})

function uniqueBy<T>(array: Array<T>, key: (item: T) => string | number): Array<T> {
  const seen = new Set()
  return array.filter((item) => {
    const k = key(item)
    return seen.has(k) ? false : seen.add(k)
  })
}
